import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import WalletPageShell from "../../Components/Wallet/WalletPageShell";
import {
  fetchClients,
  fetchWhiteLabel,
  saveWhiteLabel,
} from "../../Apis/Wallet-api";

const DEFAULTS = {
  brand_name: "",
  logo_url: "",
  primary_color: "#0891B2",
  support_email: "",
  custom_domain: "",
  footer_text: "",
};

export default function WhiteLabelSettingsPage() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [form, setForm] = useState(DEFAULTS);
  const [loadingClients, setLoadingClients] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingClients(true);
      try {
        const rows = await fetchClients();
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setClients(list);
        if (list.length === 1) {
          setClientId(list[0].client_id);
        }
      } catch (e) {
        if (!cancelled) {
          toast.error(
            e?.response?.data?.message || e?.message || "Failed to load clients."
          );
          setClients([]);
        }
      } finally {
        if (!cancelled) setLoadingClients(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!clientId) {
      setForm(DEFAULTS);
      setError("");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const data = await fetchWhiteLabel(Number(clientId));
        if (cancelled) return;
        setForm({
          brand_name: data?.brand_name || "",
          logo_url: data?.logo_url || "",
          primary_color: data?.primary_color || DEFAULTS.primary_color,
          support_email: data?.support_email || "",
          custom_domain: data?.custom_domain || "",
          footer_text: data?.footer_text || "",
        });
      } catch (e) {
        if (!cancelled) {
          setError(
            e?.response?.data?.message ||
              e?.message ||
              "Failed to load white-label settings."
          );
          setForm(DEFAULTS);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const save = async () => {
    if (!clientId) {
      toast.error("Select a client first.");
      return;
    }
    setSaving(true);
    try {
      const data = await saveWhiteLabel({
        ...form,
        client_id: Number(clientId),
      });
      setForm({
        brand_name: data?.brand_name || "",
        logo_url: data?.logo_url || "",
        primary_color: data?.primary_color || DEFAULTS.primary_color,
        support_email: data?.support_email || "",
        custom_domain: data?.custom_domain || "",
        footer_text: data?.footer_text || "",
      });
      toast.success("White-label settings saved.");
    } catch (e) {
      toast.error(
        e?.response?.data?.message || e?.message || "Failed to save settings."
      );
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, props = {}) => (
    <TextField
      label={label}
      value={form[key]}
      onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
      fullWidth
      size="small"
      disabled={!clientId || loading}
      {...props}
    />
  );

  return (
    <WalletPageShell
      heading="White label settings"
      subtitle="Client branding for wallet ops presentation"
    >
      <Stack spacing={2} maxWidth={520}>
        <FormControl fullWidth size="small" disabled={loadingClients}>
          <InputLabel>Client</InputLabel>
          <Select
            label="Client"
            value={clientId || ""}
            onChange={(e) => setClientId(e.target.value)}
          >
            {clients.map((c) => (
              <MenuItem key={c.client_id} value={c.client_id}>
                {c.client_name} (#{c.client_id})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {error ? (
          <Alert severity="error" variant="outlined">
            {error}
          </Alert>
        ) : null}

        {!clientId && !loadingClients ? (
          <Typography color="text.secondary" variant="body2">
            Select a client to view or edit white-label branding.
          </Typography>
        ) : null}

        {loading ? (
          <CircularProgress size={28} />
        ) : clientId ? (
          <>
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={2}>
                {field("brand_name", "Brand name")}
                {field("logo_url", "Logo URL")}
                {field("primary_color", "Primary color")}
                {field("support_email", "Support email")}
                {field("custom_domain", "Custom domain")}
                {field("footer_text", "Footer text", {
                  multiline: true,
                  minRows: 2,
                })}
                <Button
                  variant="contained"
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save settings"}
                </Button>
              </Stack>
            </Paper>
            <Paper
              variant="outlined"
              sx={{ p: 2, bgcolor: form.primary_color, color: "#fff" }}
            >
              <Typography variant="subtitle1">
                {form.brand_name || "Your brand"}
              </Typography>
              <Typography variant="caption">
                {form.footer_text || "Preview header strip"}
              </Typography>
            </Paper>
          </>
        ) : null}
      </Stack>
    </WalletPageShell>
  );
}
